/**
 * Rounds a date up to the next interval (in minutes)
 */
export function roundToNextInterval(date: Date, intervalMinutes: number = 5): Date {
  const result = new Date(date);
  const currentMinutes = result.getMinutes();
  const nextInterval = Math.ceil(currentMinutes / intervalMinutes) * intervalMinutes;

  if (nextInterval >= 60) {
    result.setHours(result.getHours() + 1);
    result.setMinutes(0);
  } else {
    result.setMinutes(nextInterval);
  }

  return result;
}

/**
 * Checks if a date is in the past
 */
export function isInPast(date: Date): boolean {
  return date < new Date();
}

/**
 * Adjusts a date to ensure it's in the future
 */
export function ensureFutureDate(date: Date, intervalMinutes: number = 5): Date {
  const now = new Date();
  const result = new Date(date);

  if (result <= now) {
    // If it's the same day and hour, adjust minutes to next available interval
    if (
      result.getDate() === now.getDate() &&
      result.getMonth() === now.getMonth() &&
      result.getFullYear() === now.getFullYear() &&
      result.getHours() === now.getHours()
    ) {
      return roundToNextInterval(now, intervalMinutes);
    } else {
      // If it's a different hour/day and still in the past, set to next hour
      const nextHour = new Date(now);
      nextHour.setHours(now.getHours() + 1);
      nextHour.setMinutes(0);
      return nextHour;
    }
  }

  return result;
}

/**
 * Gets the 12-hour format hour (1-12) from a Date
 */
export function get12HourFormat(date: Date): number {
  return date.getHours() % 12 === 0 ? 12 : date.getHours() % 12;
}

/**
 * Checks if a time is AM or PM
 */
export function getAmPm(date: Date): 'AM' | 'PM' {
  return date.getHours() >= 12 ? 'PM' : 'AM';
}

/**
 * Sets the hour on a date, accounting for AM/PM
 */
export function setHour(date: Date, hour: number, ampm: 'AM' | 'PM'): Date {
  const result = new Date(date);
  const isPM = ampm === 'PM';
  result.setHours(isPM ? (hour === 12 ? 12 : hour + 12) : hour === 12 ? 0 : hour);
  return result;
}

/**
 * Creates a default time for a selected date
 */
export function createDefaultTimeForDate(selectedDate: Date, disablePastDates: boolean): Date {
  const result = new Date(selectedDate);
  const now = new Date();

  // If the selected date is today and we need to disable past dates
  if (disablePastDates && selectedDate.toDateString() === now.toDateString()) {
    let hour = now.getHours();

    // If we're already past the hour, move to the next hour
    if (now.getMinutes() > 0) {
      hour = hour + 1;
    }

    // If we're at the end of the day, move to the next day at 9 AM
    if (hour >= 23) {
      result.setDate(result.getDate() + 1);
      hour = 9;
    }

    result.setHours(hour, 0);
  } else {
    // For future dates, default to 12 PM
    result.setHours(12, 0);
  }

  return result;
}

const RELATIVE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 31_536_000_000],
  ['month', 2_592_000_000],
  ['week', 604_800_000],
  ['day', 86_400_000],
  ['hour', 3_600_000],
  ['minute', 60_000],
];

/**
 * Localised "40 minutes ago" for an ISO timestamp, or null when it does not parse. Pass
 * i18next's `resolvedLanguage` so Intl never sees a detector artefact like `en_US`.
 */
export function relativeTime(iso: string, locale: string): string | null {
  const elapsed = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(elapsed)) return null;

  const formatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const unit = RELATIVE_UNITS.find(([, ms]) => Math.abs(elapsed) >= ms);
  if (!unit) return formatter.format(0, 'minute');

  return formatter.format(-Math.round(elapsed / unit[1]), unit[0]);
}
